<template lang="pug">
div(
    :id="id"
    ref="input"
    v-ripple="{ color: 'rgba(0, 0, 0, 0.1)' }"
    class="tl-input-switch"
    :class="{ active: modelState, disabled: disabledC, errorState: errorC, large: largeC, small: smallC, external: externalC }"
    :style="style"
    data-form-validator
    tabindex="0"
    @click="(e) => click(e)"
    @mouseleave="hover = false"
    @mouseover="hover = true"
)
    div(class="tl-input-switch-circle")
    div(
        :class="{ active: modelState }"
        :style="{ background: `linear-gradient(to right, var(--color-${color}) 70%, transparent)` }"
        class="tl-input-switch-background"
    )
</template>

<script setup>
import { ref, computed, watch, inject, onMounted } from 'vue'
import uniqid from 'uniqid'
import useRules from './validator/rules'
import { getInjects } from '@/components/$tl/utils/injects'
import { promiseTimeout } from '@vueuse/core'
import { useEventBus } from '@vueuse/core'

const props = defineProps({
    color: String,
    modelValue: Boolean,
    disabled: Boolean,
    large: Boolean,
    small: Boolean,
    error: Boolean,
    external: Boolean,
    rules: Array,
})

const emit = defineEmits(['resetError', 'submit', 'validated', 'update:modelValue'])

const injects = getInjects(props, inject)
const { validateLogic } = useRules()

const id = ref(uniqid())
const input = ref(null)
const modelState = ref(props.modelValue)
const internalError = ref(null)
const hover = ref(false)

const colorC = computed(() => props.color ?? injects.color?.value ?? 'tl-blue')
const disabledC = computed(() => props.disabled || injects.disabled?.value)
const largeC = computed(() => props.large || injects.large?.value)
const smallC = computed(() => props.small || injects.small?.value)
const turnedOff = computed(() => disabledC.value)
const externalC = computed(() => props.external || injects.disabled?.value)
const errorC = computed(() => (props.error ? props.error : internalError.value))
const modelValue = computed(() => props.modelValue ?? injects.modelValue?.value)

const style = computed(() => {
    const out = {}
    if (turnedOff.value) {
        out.backgroundColor = 'var(--color-tint-extralight)'
    } else if (errorC.value && !modelState.value) {
        out.backgroundColor = hover.value ? 'var(--color-error-tint-medium)' : 'var(--color-error-tint-normal)'
    } else if (modelState.value) {
        out.backgroundColor = `var(--color-${colorC.value})`
    } else {
        out.backgroundColor = hover.value ? 'var(--color-tint-medium)' : 'var(--color-tint-normal)'
    }
    return out
})

const click = async (e) => {
    e.preventDefault()
    await promiseTimeout(50)
    if (!turnedOff.value) modelState.value = !modelState.value
}

const resetError = () => {
    internalError.value = null
    emit('resetError')
}

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
})

watch(modelState, (val) => {
    if (val && !externalC.value) {
        resetError()
    }
    emit('update:modelValue', val)
})

onMounted(() => {
    // if (props.select) focus()

    id.value = uniqid()
    useEventBus(`validate-${id.value}`).on(() => {
        validateLogic(props, modelState, input, internalError, emit)
    })

    useEventBus(`clear-error-${id.value}`).on(() => {
        resetError()
    })
})
</script>

<style lang="sass" scoped>
.tl-input-switch
    width: 40px
    height: 24px
    border-radius: 20px
    cursor: pointer
    transition: var(--transition-default)
    padding: 3px
    position: relative
    z-index: 1
    overflow: hidden
    .tl-input-switch-circle
        width: 18px
        height: 18px
        border-radius: 50%
        background-color: var(--color-white)
        position: relative
        z-index: 2
        transition: var(--transition-default)
    .tl-input-switch-background
        width: 100%
        position: absolute
        height: 100%
        transform: translateX(-100%)
        background-color: red
        top: 0
        left: 0
        transition: all .4s ease-in-out
        &.active
            width: 200%
            transform: translateX(0%)
    &.active
        .tl-input-switch-circle
            transform: translateX(16px)
    &.disabled
        cursor: not-allowed
        .tl-input-switch-circle
            background-color: var(--color-white-tint-heavy)
    &.errorState
        animation: shake 0.52s cubic-bezier(.36,.07,.19,.97) both .32s
    &.large
        width: 54px
        height: 32px
        padding: 4px
        .tl-input-switch-circle
            width: 24px
            height: 24px
        &.active
            .tl-input-switch-circle
                transform: translateX(22px)
    &.external
        pointer-events: none
    &.small
        width: 34px
        height: 18px
        padding: 2px
        .tl-input-switch-circle
            width: 14px
            height: 14px
        &.active
            .tl-input-switch-circle
                transform: translateX(16px)
</style>
